import { Money, MoneySchema, moneyAdd } from "@redotech/money";
import { Currency, isCurrency } from "@redotech/money/currencies";
import * as z from "zod";

/**
 * How a duties amount relates to the email's order total:
 * - `additive` — the customer paid it on top of the subtotal, so it renders as
 *   its own summing rows. This is how duties recorded on the Shopify order
 *   behave: Shopify keeps them in `current_total_duties_set`, excluded from the
 *   subtotal.
 * - `inclusive` — the amount is already inside a line the email totals, so it
 *   renders as a non-summing "Includes $X" note. Redo's own landed cost bakes
 *   duty into the product price or the shipping rate; an additive row would
 *   double-count.
 *
 * Each source carries a primary duty plus its own secondary import charge —
 * `additionalFees` for the Shopify-order path, `taxes` for the Redo-landed-cost
 * path (often the larger of the two for VAT/GST countries).
 */
export type OrderDutiesBreakdown =
  | { presentation: "additive"; duties: Money; additionalFees: Money }
  | { presentation: "inclusive"; duties: Money; taxes: Money };

export const orderDutiesBreakdownSchema: z.ZodType<OrderDutiesBreakdown> =
  z.discriminatedUnion("presentation", [
    z.object({
      presentation: z.literal("additive"),
      duties: MoneySchema,
      additionalFees: MoneySchema,
    }),
    z.object({
      presentation: z.literal("inclusive"),
      duties: MoneySchema,
      taxes: MoneySchema,
    }),
  ]);

/**
 * Convert a raw Shopify money set's presentment money to `Money`. Returns null
 * when the set is absent or carries an unrecognized currency.
 */
export function moneySetToMoney(
  set:
    | {
        presentment_money?: {
          amount: number | string;
          currency_code?: string;
        } | null;
      }
    | null
    | undefined,
): Money | null {
  const presentmentMoney = set?.presentment_money;
  const currencyCode = presentmentMoney?.currency_code;
  if (!presentmentMoney || !currencyCode || !isCurrency(currencyCode)) {
    return null;
  }
  return {
    amount:
      typeof presentmentMoney.amount === "number"
        ? presentmentMoney.amount
        : parseFloat(presentmentMoney.amount),
    currency: currencyCode,
  };
}

export interface GetOrderDutiesInput {
  /** Email/order presentment currency; all returned `Money` is in this currency. */
  presentmentCurrency: Currency;
  /**
   * Duties recorded on the Shopify order itself, read from its presentment money
   * sets (`current_total_duties_set`, `current_total_additional_fees_set`). A
   * separate customer charge excluded from the subtotal. Populated by a
   * third-party DDP provider that writes to Shopify (e.g. Global-e). Each field
   * is `undefined` when the corresponding set is absent.
   */
  shopifyOrderDuties:
    | { duties: Money | undefined; additionalFees: Money | undefined }
    | undefined;
  /**
   * Duties + taxes the customer paid under Redo's own landed-cost guarantee,
   * pre-summed from the order's selected checkout shipping options (see
   * `getCustomerPaidLandedCost`). Already `Money` in whatever currency we stored
   * — no conversion here. Baked into the order price, so presented inclusively.
   */
  redoLandedCost: { duties: Money; taxes: Money } | undefined;
}

/**
 * Get the duties to display in an order-summary email. Duties recorded on
 * the Shopify order (a separate customer charge) take precedence over Redo's own
 * landed cost (baked into the order). Returns `null` when there are no duties to
 * show, so callers render nothing — matching the Global-e snippet's `{% if %}`
 * guard.
 */
export function getOrderDuties(
  input: GetOrderDutiesInput,
): OrderDutiesBreakdown | null {
  const { presentmentCurrency } = input;
  const zero = zeroMoney(presentmentCurrency);

  const recordedOnOrder = getRecordedOnOrder(input.shopifyOrderDuties, zero);
  if (recordedOnOrder) {
    return recordedOnOrder;
  }

  return getComputedLandedCost(input.redoLandedCost);
}

export type OrderDutiesDisplayPartKind =
  | "duties"
  | "additionalFees"
  | "importCharges";

export interface OrderDutiesDisplay {
  presentation: OrderDutiesBreakdown["presentation"];
  parts: { kind: OrderDutiesDisplayPartKind; amount: Money }[];
}

/**
 * Reduce a breakdown to the nonzero labeled-amount parts a UI renders. Shared by
 * the order email and the customer-portal totals so both show the same
 * components in the same order; each surface owns its own label strings, casing,
 * and money formatting.
 *
 * Additive (Shopify-recorded) duties stay split into their own summing rows.
 * Inclusive (Redo landed cost) duties and import taxes collapse into one
 * `importCharges` figure — they're baked into the order total, so a single
 * combined note reads cleanly and never competes with the email's sales-Tax row.
 */
export function getOrderDutiesDisplayParts(
  breakdown: OrderDutiesBreakdown,
): OrderDutiesDisplay {
  const parts: { kind: OrderDutiesDisplayPartKind; amount: Money }[] = [];
  if (breakdown.presentation === "additive") {
    if (isPositive(breakdown.duties)) {
      parts.push({ kind: "duties", amount: breakdown.duties });
    }
    if (isPositive(breakdown.additionalFees)) {
      parts.push({ kind: "additionalFees", amount: breakdown.additionalFees });
    }
  } else {
    const importCharges = moneyAdd(breakdown.duties, breakdown.taxes);
    if (isPositive(importCharges)) {
      parts.push({ kind: "importCharges", amount: importCharges });
    }
  }
  return { presentation: breakdown.presentation, parts };
}

function getRecordedOnOrder(
  shopifyOrderDuties: GetOrderDutiesInput["shopifyOrderDuties"],
  zero: Money,
): OrderDutiesBreakdown | null {
  if (!shopifyOrderDuties) {
    return null;
  }
  const duties = shopifyOrderDuties.duties ?? zero;
  const additionalFees = shopifyOrderDuties.additionalFees ?? zero;
  // Precedence, not display: recorded duties only outrank landed cost when
  // actually charged, so a $0 set falls through and an LCG order still shows.
  if (!isPositive(duties) && !isPositive(additionalFees)) {
    return null;
  }
  return { presentation: "additive", duties, additionalFees };
}

function getComputedLandedCost(
  redoLandedCost: GetOrderDutiesInput["redoLandedCost"],
): OrderDutiesBreakdown | null {
  if (!redoLandedCost) {
    return null;
  }
  const { duties, taxes } = redoLandedCost;
  if (!isPositive(duties) && !isPositive(taxes)) {
    return null;
  }
  return { presentation: "inclusive", duties, taxes };
}

function zeroMoney(currency: Currency): Money {
  return { amount: 0, currency };
}

function isPositive(money: Money): boolean {
  return money.amount > 0;
}
